import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "Home", href: "#top", testId: "nav-home-link" },
  { label: "Spices", href: "#products", testId: "nav-spices-link" },
  { label: "Cold Pressed Oils", href: "#oils", testId: "nav-oils-link" },
  { label: "For Home", href: "#shop", testId: "nav-shop-link" },
  { label: "About", href: "#about", testId: "nav-about-link" },
  { label: "Why Us", href: "#why-us", testId: "nav-why-us-link" },
  { label: "FAQ", href: "#faq", testId: "nav-faq-link" },
  { label: "Contact", href: "#contact", testId: "nav-contact-link" },
];

export const Wordmark = ({ dark = false }) => (
  <span className="leading-none inline-block">
    <span className={`font-serif text-[22px] font-bold tracking-[0.14em] ${dark ? "text-cream" : "text-ink"}`}>
      TSUGIVA
    </span>
    <span className="mt-0.5 block text-[8.5px] font-semibold uppercase tracking-[0.4em] text-gold">
      International Traders
    </span>
  </span>
);

const scrollTo = (e, href) => {
  e.preventDefault();
  document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-cream/85 backdrop-blur-md border-b border-stone2 shadow-[0_8px_30px_rgba(26,23,20,0.06)]" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3.5 sm:px-8 lg:px-16">
        <a href="#top" data-testid="nav-logo-link" onClick={(e) => scrollTo(e, "#top")}>
          <Wordmark dark={!scrolled && !open} />
        </a>

        <div className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testId}
              onClick={(e) => scrollTo(e, l.href)}
              className={`text-sm font-medium tracking-wide transition-colors duration-300 hover:text-gold ${
                scrolled ? "text-smoke" : "text-cream/85"
              }`}
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/oil-story"
            data-testid="nav-oil-story-link"
            className={`text-sm font-medium tracking-wide transition-colors duration-300 hover:text-gold ${
              scrolled ? "text-smoke" : "text-cream/85"
            }`}
          >
            Oil Story
          </Link>
          <a
            href="#quote"
            data-testid="nav-get-quote-button"
            onClick={(e) => scrollTo(e, "#quote")}
            className="rounded-full bg-chilli px-5 py-2.5 text-sm font-semibold text-cream transition-transform duration-300 hover:scale-[1.04]"
          >
            Get a Quote
          </a>
        </div>

        <button
          data-testid="nav-mobile-menu-button"
          className={`lg:hidden ${scrolled || open ? "text-ink" : "text-cream"}`}
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-stone2 bg-cream/95 backdrop-blur-md lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-5">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace(" ", "-")}-link`}
                  onClick={(e) => {
                    scrollTo(e, l.href);
                    setOpen(false);
                  }}
                  className="rounded-lg px-2 py-3 text-base font-medium text-smoke hover:bg-sand"
                >
                  {l.label}
                </a>
              ))}
              <Link
                to="/oil-story"
                data-testid="nav-mobile-oil-story-link"
                onClick={() => setOpen(false)}
                className="rounded-lg px-2 py-3 text-base font-medium text-smoke hover:bg-sand"
              >
                Oil Story
              </Link>
              <a
                href="#quote"
                data-testid="nav-mobile-get-quote-button"
                onClick={(e) => {
                  scrollTo(e, "#quote");
                  setOpen(false);
                }}
                className="mt-2 rounded-full bg-chilli px-5 py-3 text-center text-sm font-semibold text-cream"
              >
                Get a Quote
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
